import { set, union, unionBy } from "es-toolkit/compat";

/**
 * Given an array of objects, returns an array of unique object[key] elements
 *
 * @param {Object[]} objects
 * @param {string} key - string of object property to be aggregated
 * @param {string} uniqueBy - optional, if object[key] is an object/array of objects
 *  what property to check for uniqueness
 * @returns {Object[] | string[]}
 */
export function GetAllObjectValue<
  T extends Array<Record<K, any>>,
  K extends keyof T[number],
  U extends keyof T[number][K],
>(objects: T, key: K, uniqueBy?: U): Array<T[K]> {
  return objects.reduce<Array<T[K]>>((values, object) => {
    const currentValue = Array.isArray(object[key])
      ? object[key]
      : [object[key]];
    if (uniqueBy) {
      return unionBy<T[K]>(values, currentValue, uniqueBy);
    } else {
      return union<T[K]>(values, currentValue);
    }
  }, []);
}

/**
 * The reverse of the flattenObject function. Given object with `dot noation`
 * properties, returns an object with nested properties e.g.
 *   { a: 1, "b.c": 2, "b.d": 3} => { a: 1, b: { c: 2, d: 3 }
 *
 * pickListProperties have their values expanded to { id: value, name: value }
 *  e.g. with pickListProperties = ["b.c"],
 *   { a: 1, "b.c": 2, "b.d": 3} => { a: 1, b: { c: { id: 2 }, d: 3 } }
 *
 * @param {Object.<string, any>} object The object with "dot notation" properties
 * @deprecated use toExpandedObject from @validereinc/utilities instead
 */
export const expandObject = (object: Record<string, string>) => {
  const result: Record<string, string> = {};

  Object.entries(object).forEach(([key, value]: [string, string]) => {
    set(result, key, value);
  });

  return result;
};
